import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Zap, Clock, Globe, Languages, Star, Play, Pause,
  Instagram, Facebook, Youtube, Twitter, Linkedin, Music2, Settings, Calendar,
  Sparkles, CheckCircle, AlertCircle, Loader2, Info,
  Trash2, RotateCcw, History, BarChart3, RefreshCw, XCircle, Users, Database,
  ChevronUp, ChevronDown, Mic, Mail,
} from 'lucide-react';
import { autoPostAPI, videoAPI, socialAPI, justimmoExportAPI, propertiesAPI, profileAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import AppLayout from '../components/AppLayout';
import AutoPostMusicSection from '../components/AutoPostMusicSection';
import CaptionStyleSection from '../components/CaptionStyleSection';
import AutoPostItemDetailModal from '../components/AutoPostItemDetailModal';
import VoiceSelectSection from '../components/VoiceSelectSection';

// TikTok icon component (not in lucide-react)
const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

// Weekday picker for the scheduler (#18). Backend uses 0=Mon … 6=Sun.
const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
function WeekdayPicker({ value, onChange, disabled, size = 'md' }) {
  const days = Array.isArray(value) ? value : [0, 1, 2, 3, 4, 5, 6];
  const toggle = (d) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b);
    onChange(next);
  };
  const dim = size === 'sm' ? 'w-7 h-7 text-[11px]' : 'w-9 h-9 text-xs';
  return (
    <div className={`flex flex-wrap gap-1.5 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      {WEEKDAY_LABELS.map((lbl, i) => {
        const active = days.includes(i);
        return (
          <button
            key={i}
            type="button"
            aria-pressed={active}
            onClick={() => toggle(i)}
            className={`${dim} rounded-md font-medium transition-colors ${
              active
                ? 'bg-[#1f4d3a] text-white'
                : 'bg-[#fffdf7] border border-[#d8d0bd] text-[#6f6a5c] hover:border-[#1f4d3a]/50'
            }`}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

// Platform config for auto-post
const AUTO_POST_PLATFORMS = [
  { key: 'instagram', name: 'Instagram', label: 'Reels', icon: Instagram, activeColor: 'text-pink-400', borderColor: 'border-pink-500', bgColor: 'bg-pink-500/10' },
  { key: 'facebook', name: 'Facebook', label: 'Reels', icon: Facebook, activeColor: 'text-blue-400', borderColor: 'border-blue-500', bgColor: 'bg-blue-500/10' },
  { key: 'tiktok', name: 'TikTok', label: 'Videos', icon: TikTok, activeColor: 'text-cyan-400', borderColor: 'border-cyan-500', bgColor: 'bg-cyan-500/10' },
  { key: 'youtube', name: 'YouTube', label: 'Shorts / Video', icon: Youtube, activeColor: 'text-[#b3543f]', borderColor: 'border-[#b3543f]', bgColor: 'bg-[#f7e8e3]' },
  { key: 'x', name: 'X', label: 'Post', icon: Twitter, activeColor: 'text-[#4a463b]', borderColor: 'border-[#b5ad99]', bgColor: 'bg-[#b5ad99]/10' },
  { key: 'linkedin', name: 'LinkedIn', label: 'Video post', icon: Linkedin, activeColor: 'text-sky-400', borderColor: 'border-sky-500', bgColor: 'bg-sky-500/10' },
];

// Helper to render platform icon by name (for history/queue lists)
const PlatformIcon = ({ platform, className }) => {
  const config = AUTO_POST_PLATFORMS.find(p => p.key === platform);
  if (!config) return null;
  const Icon = config.icon;
  return <Icon className={`${className} ${config.activeColor}`} />;
};

export default function AutoPostSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState({ items: [], stats: {} });
  const [connectedSocials, setConnectedSocials] = useState([]);
  const [propertyCount, setPropertyCount] = useState(0);
  // Profile snapshot for the auto-post hard-gate (v1.6). Without
  // name + a profile image (or company logo), the renderer falls back
  // to the placeholder "demo agent" branding and auto-posts ship a
  // string of videos with no actual realtor in them. Embarrassing
  // even for the Maklers who never opened settings. Gating the toggle
  // on profile completeness here is the smallest enforcement seam.
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [refreshing, setRefreshing] = useState(false);

  const [config, setConfig] = useState({
    is_enabled: false,
    posts_per_day: 1,
    preferred_time: '10:00',
    timezone: 'Europe/Vienna',
    language: 'de',
    favorite_templates: [],
    platforms: ['instagram', 'facebook'],
    auto_select_template: true,
    auto_generate_caption: true,
    auto_generate_hashtags: true,
    enable_voiceover: true,
    voice_id: null,
    // Voiceover script controls. instructions = free-text guidance for the
    // AI; include_brand = name the business in the spoken CTA; mention_price
    // = allow price/numbers in the voiceover (off by default — kept emotional).
    voiceover_instructions: '',
    voiceover_include_brand: true,
    voiceover_mention_price: false,
    preview_mode: 'auto_with_preview',
    // Music selection. Multi-select preset ids + single optional MP3
    // upload. The scheduler picks one at random per render. Was
    // previously only settable via the onboarding wizard's MusicStep;
    // the AutoPostMusicSection (rendered in the Settings tab below)
    // exposes the same field shapes so users can rotate music later.
    background_music_preset_ids: [],
    background_music_url: null,
    // Caption style. See platform_formatter.CaptionStyle + the
    // CaptionStyleSection component. Defaults work for ~90% of users;
    // they only tweak when they want a specific voice/CTA preference,
    // or after running the Brand Voice Analyzer.
    caption_style: {
      brand_voice: 'professional',
      cta_style: 'engagement',
      hashtag_aggression: 'standard',
      emoji_usage: 'moderate',
      custom_instructions: '',
      custom_hashtags: [],
      blocked_hashtags: [],
      learned_brand_voice: null,
    },
  });
  const [platformDaysOpen, setPlatformDaysOpen] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);
  // Detail modal. Opens when the user clicks a queue or history row.
  // Holds the full item dict so we can render the video player +
  // editable captions without re-fetching. Cleared on close.
  const [detailItem, setDetailItem] = useState(null);
  // Snapshot of the last-saved config. Used by the floating save bar
  // to detect unsaved changes (dirty state). On every successful save
  // we update this snapshot so the bar disappears. On Reset we restore
  // `config` from this snapshot. Storing as a JSON string makes deep-
  // equality comparison cheap on every render. The config tree is
  // small (a few dozen primitive fields + a few arrays).
  const [savedConfigJson, setSavedConfigJson] = useState('');

  const loadData = useCallback(async () => {
    // 401 safety net: if we don't have a token locally, don't even try.
    // The global interceptor would redirect on 401, but skipping avoids
    // the brief flash of "loading" + extra network noise. Important: the
    // localStorage key is `auth_token`, not `token`. The earlier typo
    // silently left every user with a never-ending spinner.
    if (!localStorage.getItem('auth_token')) return;
    setLoading(true);
    try {
      const [configRes, templatesRes, queueRes, historyRes, socialsRes, propsRes, profileRes] = await Promise.all([
        autoPostAPI.getConfig(),
        videoAPI.getTemplates(),
        autoPostAPI.getQueue(),
        autoPostAPI.getHistory(),
        socialAPI.getAccounts(),
        propertiesAPI.getAll().catch(() => ({ data: [] })),
        // Profile feeds the hard-gate below. Non-fatal if it errors –
        // the gate just stays restrictive (treats profile as empty),
        // which is the safe default for an unverified state.
        profileAPI.get().catch(() => ({ data: null })),
      ]);

      const loadedConfig = configRes.data || {};
      setConfig(loadedConfig);
      // Snapshot the loaded config so the floating save bar knows what
      // "saved" looks like. The bar appears whenever the user's edits
      // diverge from this snapshot and hides again on Save / Reset.
      setSavedConfigJson(JSON.stringify(loadedConfig));
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data.filter(t => t.media_type === 'image') : []);
      setQueue(Array.isArray(queueRes.data) ? queueRes.data : []);
      setHistory(historyRes.data || { items: [], stats: {} });
      setConnectedSocials(Array.isArray(socialsRes.data?.accounts) ? socialsRes.data.accounts : []);
      const rawProps = Array.isArray(propsRes.data) ? propsRes.data : [];
      setPropertyCount(rawProps.length);
      // Slim version for the caption preview modal's property picker.
      // Keeps the network payload small for the modal. No images, etc.
      setPropertyOptions(rawProps.map((p) => ({ id: p.id, title: p.title })));
      setProfile(profileRes?.data || null);
    } catch (error) {
      // Surface non-401 errors to the user (global interceptor handles 401).
      if (error?.response?.status !== 401) {
        console.error('Failed to load auto post settings:', error);
        toast.error(t('autoPost.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh queue and history every 30 seconds.
  // Stops polling as soon as auth is cleared so we don't spam 401s.
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!localStorage.getItem('auth_token')) {
        clearInterval(interval);
        return;
      }
      try {
        const [queueRes, historyRes] = await Promise.all([
          autoPostAPI.getQueue(),
          autoPostAPI.getHistory(),
        ]);
        setQueue(Array.isArray(queueRes.data) ? queueRes.data : []);
        setHistory(historyRes.data || { items: [], stats: {} });
      } catch (e) {
        // Silent on background refresh. 401 handled by the global axios
        // interceptor (redirects to /auth). Transient network errors shouldn't
        // nag the user; they'll see the next tick succeed.
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await autoPostAPI.updateConfig(config);
      toast.success(t('autoPost.saved'));
      // Snapshot the saved state so the floating "unsaved changes" bar
      // disappears. Server returns the canonical-shaped config; we
      // prefer that over our local one in case the server normalized
      // anything (e.g. resolved music URLs that the client didn't
      // know about).
      const fresh = res?.data || config;
      setConfig(fresh);
      setSavedConfigJson(JSON.stringify(fresh));
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || t('autoPost.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** Discard unsaved changes. Restore config from the last-saved snapshot.
   * Triggered by the "Verwerfen" button on the floating save bar.
   * Doesn't touch the server. */
  const handleDiscardChanges = () => {
    if (!savedConfigJson) return;
    try {
      setConfig(JSON.parse(savedConfigJson));
      toast.success(t('autoPost.discarded') || 'Änderungen verworfen.');
    } catch {/* malformed snapshot. Silently ignore */}
  };

  // Dirty state. Does the current config differ from the last saved?
  // Memoized so it doesn't re-stringify on unrelated state changes.
  // Used by the FloatingSaveBar to decide whether to slide up.
  const currentJson = useMemo(() => JSON.stringify(config), [config]);
  const isDirty = currentJson !== savedConfigJson && savedConfigJson !== '';

  const hasSocials = connectedSocials.length > 0;

  // v1.6. Auto-post HARD-GATE.
  //
  // Until every prerequisite is in place we refuse to flip the toggle on
  // (already-enabled users can still see/toggle off, naturally). The four
  // checks below cover the cases where enabling auto-post would queue
  // visibly-broken renders:
  //
  //   * `hasName`          . Without a name, the agent-card slot in
  //                            voiceover-bearing templates says "–".
  //   * `hasBranding`      . Without a profile photo OR company logo,
  //                            the "agent" outro/intro uses the demo
  //                            placeholder (the exact "screws up my
  //                            renders" complaint that prompted this).
  //   * `hasSocials`       . Nothing to post to. The backend rejects
  //                            enable anyway but pre-checking here is
  //                            what makes the gate feel honest.
  //   * `propertyCount > 0`. Nothing to render. Same backend gate.
  //
  // We collect the *missing* items so the disabled-state tooltip can be
  // specific ("Profilfoto fehlt · 0 Plattformen verbunden") rather than
  // generic ("Setup unvollständig"). Specific = the user knows exactly
  // what to fix instead of hunting through settings.
  const hasName = !!(profile?.name && profile.name.trim().length > 0);
  const hasBranding = !!(profile?.profile_image || profile?.company_logo);
  const setupMissing = [
    !hasName && (t('autoPost.gateMissingName') || 'Name im Profil fehlt'),
    !hasBranding && (t('autoPost.gateMissingBranding') || 'Profilfoto oder Logo fehlt'),
    !hasSocials && (t('autoPost.gateMissingSocials') || 'Keine Plattformen verbunden'),
    propertyCount === 0 && (t('autoPost.gateMissingProperties') || 'Keine Immobilie importiert'),
  ].filter(Boolean);
  const setupBlocking = setupMissing.length > 0;
  // The toggle is hard-disabled ONLY when (a) setup is incomplete AND
  // (b) auto-post is currently off. Already-enabled users keep the
  // ability to switch off even if their setup drifted (e.g. they
  // disconnected their last social). Locking them in would be worse.
  const toggleHardDisabled = setupBlocking && !config.is_enabled;

  const handleToggleTemplate = (templateId) => {
    const favorites = config.favorite_templates || [];
    if (favorites.includes(templateId)) {
      setConfig({ ...config, favorite_templates: favorites.filter(id => id !== templateId) });
    } else {
      setConfig({ ...config, favorite_templates: [...favorites, templateId] });
    }
  };

  const handleTogglePlatform = (platform) => {
    const platforms = config.platforms || [];
    if (platforms.includes(platform)) {
      if (platforms.length > 1) {
        setConfig({ ...config, platforms: platforms.filter(p => p !== platform) });
      } else {
        toast.error(t('autoPost.minOnePlatform'));
      }
    } else {
      setConfig({ ...config, platforms: [...platforms, platform] });
    }
  };

  const handleGeneratePost = async () => {
    try {
      const res = await autoPostAPI.generatePost();
      toast.success(t('autoPost.postCreated', { property: res.data.property }));
      // Refresh queue
      const queueRes = await autoPostAPI.getQueue();
      setQueue(queueRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('autoPost.postCreateFailed'));
    }
  };

  const handleDeleteQueueItem = async (queueId) => {
    try {
      await autoPostAPI.deleteQueueItem(queueId);
      toast.success(t('autoPost.entryDeleted'));
      setQueue(queue.filter(item => item.id !== queueId));
    } catch (error) {
      toast.error(t('autoPost.deleteFailed'));
    }
  };

  const handleRetryQueueItem = async (queueId) => {
    try {
      await autoPostAPI.retryQueueItem(queueId);
      toast.success(t('autoPost.retryStarted'));
      const queueRes = await autoPostAPI.getQueue();
      setQueue(queueRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('autoPost.retryFailed'));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [queueRes, historyRes] = await Promise.all([
        autoPostAPI.getQueue(),
        autoPostAPI.getHistory(),
      ]);
      setQueue(queueRes.data);
      setHistory(historyRes.data);
    } catch (e) {
      toast.error(t('autoPost.refreshFailed'));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#23211a] flex items-center gap-2">
              <Zap className="w-6 h-6 text-[#9a6d1f]" />
              {t('autoPost.title')}
            </h1>
            <p className="text-[#6f6a5c] text-sm">{t('autoPost.subtitle')}</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1f4d3a] hover:bg-[#163a2c]"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </div>

        {/* Skip-reason banner. Surfaces "why didn't yesterday's auto-post
            fire" so the user isn't left guessing. Backend stamps
            last_skip_reason / last_skip_detail on auto_post_configs
            whenever _maybe_queue_for_user bails (no eligible properties,
            insufficient images for chosen template, etc.) and clears
            them on the next successful queue. See audit #28. */}
        {config.is_enabled && config.last_skip_reason && (
          <Card className="border-amber-600/30 bg-amber-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#9a6d1f] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#9a6d1f]">
                    {t('autoPost.skipBanner.title') || 'Letzter Auto-Post übersprungen'}
                  </p>
                  <p className="text-xs text-[#9a6d1f]/80 mt-1">
                    {(() => {
                      const reason = config.last_skip_reason;
                      const detail = config.last_skip_detail || {};
                      if (reason === 'no_properties') {
                        return t('autoPost.skipBanner.noProperties')
                          || 'Keine geeigneten Immobilien gefunden. Stelle sicher, dass dein Justimmo-/Willhaben-Filter auf dich selbst zeigt.';
                      }
                      if (reason === 'insufficient_images') {
                        return t('autoPost.skipBanner.insufficientImages', {
                          title: detail.property_title || '?',
                          have: detail.have ?? '?',
                          need: detail.need ?? '?',
                        }) || `„${detail.property_title || '?'}" hat nur ${detail.have ?? '?'} Bilder, gewählte Vorlage braucht ${detail.need ?? '?'}. Mehr Bilder hinzufügen oder andere Vorlage als Favorit setzen.`;
                      }
                      return t('autoPost.skipBanner.generic')
                        || 'Auto-Post konnte nicht erstellt werden. Schau die Queue-Liste unten an.';
                    })()}
                  </p>
                  {config.last_skip_at && (
                    <p className="text-[11px] text-[#9a6d1f]/70 mt-1">
                      {new Date(
                        config.last_skip_at.endsWith?.('Z')
                          ? config.last_skip_at
                          : (config.last_skip_at + 'Z')
                      ).toLocaleString(undefined, {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* v1.6. Unified setup-missing banner.
            Replaces the old "no socials" single-issue warning with a
            consolidated checklist so the user sees every blocker in one
            place. Routes to /setup (the 7-step wizard) by default –
            that's the path that fixes all of them at once. Already-
            enabled users with a partial drift (e.g. they disconnected a
            social last week) still see the banner but it's softer copy
            since the gate doesn't lock the toggle off.  */}
        {setupBlocking && (
          <Card className="border-amber-600/30 bg-amber-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#9a6d1f] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#9a6d1f]">
                    {config.is_enabled
                      ? (t('autoPost.gateBannerTitleSoft') || 'Setup-Lücke entdeckt')
                      : (t('autoPost.gateBannerTitle') || 'Setup unvollständig. Auto-Post deaktiviert')}
                  </p>
                  <ul className="mt-1.5 space-y-0.5">
                    {setupMissing.map((m) => (
                      <li key={m} className="text-xs text-[#9a6d1f]/80 flex items-center gap-1.5">
                        <span className="inline-block w-1 h-1 rounded-full bg-[#c7a869]/60" />
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#9a6d1f]/50 text-[#9a6d1f] hover:bg-[#f0e2c2] shrink-0"
                  onClick={() => navigate('/setup')}
                  data-testid="auto-post-gate-setup-cta"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  {t('autoPost.gateCta') || 'Setup vervollständigen'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Toggle */}
        <Card className="glass border-[#1f4d3a]/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${config.is_enabled ? 'bg-[#d6e8dd]' : 'bg-[#e9e2d2]'}`}>
                  {config.is_enabled ? (
                    <Play className="w-6 h-6 text-[#1e7a4f]" />
                  ) : (
                    <Pause className="w-6 h-6 text-[#8a8474]" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#23211a]">
                    {config.is_enabled ? t('autoPost.enabled') : t('autoPost.disabled')}
                  </h3>
                  <p className="text-[#6f6a5c] text-sm">
                    {config.is_enabled
                      ? t('autoPost.enabledDesc', {
                          // Defaults match the backend's `config.get("posts_per_day", 1)`
                          // fallback. The config doc may not have these fields
                          // until the user explicitly saves the settings form.
                          count: config.posts_per_day ?? 1,
                          time: config.preferred_time || '10:00',
                        })
                      : t('autoPost.disabledDesc')}
                  </p>
                </div>
              </div>
              <Switch
                checked={config.is_enabled}
                disabled={toggleHardDisabled}
                title={
                  toggleHardDisabled
                    ? (t('autoPost.gateTooltipPrefix') || 'Setup unvollständig:') + ' ' + setupMissing.join(' · ')
                    : undefined
                }
                onCheckedChange={(checked) => {
                  // v1.6. Hard gate. The Switch itself is `disabled` when
                  // setup is incomplete, so this handler only runs on a
                  // legitimate enable. The redundant guards below stay as
                  // belt-and-suspenders in case a future refactor opens
                  // a path that bypasses the disabled prop.
                  if (checked && setupBlocking) {
                    toast.error(
                      (t('autoPost.gateBlocked') || 'Setup unvollständig')
                      + ': ' + setupMissing.join(' · ')
                    );
                    return;
                  }
                  setConfig({ ...config, is_enabled: checked });
                }}
                data-testid="auto-post-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#efe9db] border border-[#d8d0bd] w-full grid grid-cols-3 h-auto">
            <TabsTrigger value="settings" className="min-w-0 px-2 text-xs sm:text-sm data-[state=active]:bg-[#1f4d3a] data-[state=active]:text-white">
              <Settings className="hidden sm:inline-block w-4 h-4 mr-2 shrink-0" />
              <span className="truncate">{t('common.settings')}</span>
            </TabsTrigger>
            <TabsTrigger value="queue" className="min-w-0 px-2 text-xs sm:text-sm data-[state=active]:bg-[#1f4d3a] data-[state=active]:text-white">
              <Calendar className="hidden sm:inline-block w-4 h-4 mr-2 shrink-0" />
              <span className="truncate">{t('autoPost.queue')} ({queue.length})</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="min-w-0 px-2 text-xs sm:text-sm data-[state=active]:bg-[#1f4d3a] data-[state=active]:text-white">
              <History className="hidden sm:inline-block w-4 h-4 mr-2 shrink-0" />
              <span className="truncate">{t('autoPost.history')}</span>
            </TabsTrigger>
          </TabsList>

          {/* ─── Settings Tab ─── */}
          {/* pb-24 reserves space at the bottom so the floating
              "unsaved changes" save bar doesn't cover the last
              section's bottom when the user scrolls to it. */}
          <TabsContent value="settings" className="space-y-6 mt-6 pb-24">
            {/* Sticky section nav. Chip-style buttons that scroll-jump
                to each settings section. With 7 sections the page is
                long; users were having to scroll through everything
                to find one thing. This nav also functions as a "table
                of contents" so users see the shape of the page at a
                glance. Scroll-spy highlights the active section.
                On mobile the pills horizontal-scroll. Sits below the
                AppLayout's sticky header (top-16 = 64px). */}
            <SettingsTabNav t={t} />

            {/* Connected sources summary card. Auto-post only consumes
                whatever the user has set up under /imports. We keep the
                rich management UI in that single hub and just show a
                slim status here. Reduces this settings page's surface
                area by ~80 lines + makes the relationship between
                "where properties come from" and "what auto-post does"
                clear: imports feed auto-post, set them up once, manage
                them in one place. */}
            <section id="settings-section-sources" data-settings-section="sources" className="scroll-mt-32">
              <ConnectedSourcesSummary t={t} navigate={navigate} />
            </section>

            {/* Schedule Settings */}
            <section id="settings-section-schedule" data-settings-section="schedule" className="scroll-mt-32">
            <Card className="glass border-[#e3dccb]">
              <CardHeader>
                <CardTitle className="text-[#23211a] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#1f4d3a]" />
                  {t('autoPost.schedule')}
                </CardTitle>
                <CardDescription>{t('autoPost.scheduleDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-[#6f6a5c] mb-1.5 block">{t('autoPost.time')}</label>
                    <Input
                      type="time"
                      value={config.preferred_time}
                      onChange={(e) => setConfig({ ...config, preferred_time: e.target.value })}
                      className="bg-[#fffdf7] border-[#d8d0bd]"
                      data-testid="preferred-time-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#6f6a5c] mb-1.5 block">{t('autoPost.timezone')}</label>
                    <select
                      value={config.timezone}
                      onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
                      className="w-full h-10 rounded-md bg-[#fffdf7] border border-[#d8d0bd] text-[#23211a] px-3"
                      data-testid="timezone-select"
                    >
                      <option value="Europe/Vienna">{t('timezones.vienna')}</option>
                      <option value="Europe/Berlin">{t('timezones.berlin')}</option>
                      <option value="Europe/Zurich">{t('timezones.zurich')}</option>
                      <option value="Europe/London">{t('timezones.london')}</option>
                      <option value="America/New_York">{t('timezones.newYork')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-[#6f6a5c] mb-1.5 block">{t('autoPost.language')}</label>
                    <select
                      value={config.language}
                      onChange={(e) => setConfig({ ...config, language: e.target.value })}
                      className="w-full h-10 rounded-md bg-[#fffdf7] border border-[#d8d0bd] text-[#23211a] px-3"
                      data-testid="language-select"
                    >
                      <option value="de">Deutsch</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>

                {/* Posting days (#18) */}
                <div>
                  <label className="text-sm text-[#6f6a5c] mb-1.5 block">Wochentage</label>
                  <WeekdayPicker
                    value={config.post_days}
                    onChange={(days) => setConfig({ ...config, post_days: days.length ? days : [0, 1, 2, 3, 4, 5, 6] })}
                  />
                  <p className="text-[11px] text-[#8a8474] mt-1.5">
                    An welchen Tagen automatisch gepostet wird. Einzelne Plattformen kannst du unten bei „Plattformen" auf eigene Tage einstellen.
                  </p>
                </div>

                {/* Posts per day (#17) */}
                <div>
                  <label className="text-sm text-[#6f6a5c] mb-1.5 block">Beiträge pro Tag</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={(config.posts_per_day ?? 1) <= 1}
                      onClick={() => setConfig({ ...config, posts_per_day: Math.max(1, (config.posts_per_day || 1) - 1) })}
                      className="w-9 h-9 rounded-md bg-[#fffdf7] border border-[#d8d0bd] text-[#4a463b] hover:border-[#1f4d3a]/50 text-lg leading-none disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#c9c1ac]"
                    >−</button>
                    <span className="text-[#23211a] text-sm w-6 text-center" data-testid="posts-per-day-value">{config.posts_per_day ?? 1}</span>
                    <button
                      type="button"
                      disabled={(config.posts_per_day ?? 1) >= 3}
                      onClick={() => setConfig({ ...config, posts_per_day: Math.min(3, (config.posts_per_day || 1) + 1) })}
                      className="w-9 h-9 rounded-md bg-[#fffdf7] border border-[#d8d0bd] text-[#4a463b] hover:border-[#1f4d3a]/50 text-lg leading-none disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#c9c1ac]"
                    >+</button>
                    <span className="text-[11px] text-[#8a8474]">max. 3 pro Tag</span>
                  </div>
                </div>

                {/* Property selection criteria (#17) */}
                <div className="border-t border-[#e3dccb] pt-4 space-y-3">
                  <div>
                    <label className="text-sm text-[#6f6a5c] mb-1.5 block">Welche Immobilien zuerst?</label>
                    <select
                      value={config.property_selection_order || 'varied'}
                      onChange={(e) => setConfig({ ...config, property_selection_order: e.target.value })}
                      className="w-full h-10 rounded-md bg-[#fffdf7] border border-[#d8d0bd] text-[#23211a] px-3"
                      data-testid="selection-order-select"
                    >
                      <option value="varied">Abwechslungsreich (empfohlen)</option>
                      <option value="newest">Neueste zuerst</option>
                      <option value="oldest">Älteste zuerst</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-[#4a463b]">Nur noch nicht gepostete</p>
                      <p className="text-[11px] text-[#8a8474]">Keine Immobilie wird doppelt gepostet. Sind alle einmal dran gewesen, wartet das System auf neue Inserate.</p>
                    </div>
                    <Switch
                      checked={config.only_unposted === true}
                      onCheckedChange={(checked) => setConfig({ ...config, only_unposted: checked })}
                      data-testid="only-unposted-toggle"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            </section>

            {/* Platforms */}
            <section id="settings-section-platforms" data-settings-section="platforms" className="scroll-mt-32">
            <Card className="glass border-[#e3dccb]">
              <CardHeader>
                <CardTitle className="text-[#23211a] flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#1f4d3a]" />
                  {t('autoPost.platforms')}
                </CardTitle>
                <CardDescription>{t('autoPost.platformsDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {AUTO_POST_PLATFORMS.map(platform => {
                    const isActive = config.platforms?.includes(platform.key);
                    const Icon = platform.icon;
                    return (
                      <div
                        key={platform.key}
                        onClick={() => handleTogglePlatform(platform.key)}
                        className={`p-4 rounded-lg cursor-pointer transition-all border-2 ${
                          isActive
                            ? `${platform.borderColor} ${platform.bgColor}`
                            : 'border-[#d8d0bd] bg-[#fffdf7] hover:border-[#1f4d3a]/40'
                        }`}
                        data-testid={`platform-${platform.key}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`w-6 h-6 ${isActive ? platform.activeColor : 'text-[#8a8474]'}`} />
                          <div>
                            <p className="font-medium text-[#23211a]">{platform.name}</p>
                            <p className="text-xs text-[#8a8474]">{platform.label}</p>
                          </div>
                          {isActive && (
                            <CheckCircle className={`w-5 h-5 ${platform.activeColor} ml-auto`} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Per-platform posting days (#18). Optional overrides — a
                    platform without its own days uses the global weekdays. */}
                {(config.platforms?.length || 0) > 0 && (
                  <div className="mt-4 border-t border-[#e3dccb] pt-4">
                    <button
                      type="button"
                      onClick={() => setPlatformDaysOpen((v) => !v)}
                      className="inline-flex items-center gap-1.5 text-sm text-[#6f6a5c] hover:text-[#23211a] transition"
                    >
                      {platformDaysOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      Tage pro Plattform (optional)
                    </button>
                    {platformDaysOpen && (
                      <div className="mt-3 space-y-3">
                        <p className="text-[11px] text-[#8a8474]">
                          Standardmäßig gelten die Wochentage oben. Hier kannst du einzelne Plattformen seltener posten lassen, z.&nbsp;B. TikTok nur Mo/Mi/Fr.
                        </p>
                        {AUTO_POST_PLATFORMS.filter((p) => config.platforms?.includes(p.key)).map((platform) => {
                          const Icon = platform.icon;
                          const override = config.platform_post_days?.[platform.key];
                          const hasOverride = Array.isArray(override);
                          return (
                            <div key={platform.key} className="p-3 rounded-lg bg-[#fffdf7] border border-[#e3dccb]">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Icon className={`w-4 h-4 ${platform.activeColor}`} />
                                  <span className="text-sm text-[#23211a]">{platform.name}</span>
                                </div>
                                <label className="flex items-center gap-1.5 text-[11px] text-[#6f6a5c] cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={hasOverride}
                                    onChange={(e) => {
                                      const next = { ...(config.platform_post_days || {}) };
                                      if (e.target.checked) {
                                        next[platform.key] = config.post_days || [0, 1, 2, 3, 4, 5, 6];
                                      } else {
                                        delete next[platform.key];
                                      }
                                      setConfig({ ...config, platform_post_days: next });
                                    }}
                                    className="accent-[#1f4d3a]"
                                  />
                                  Eigene Tage
                                </label>
                              </div>
                              {hasOverride ? (
                                <WeekdayPicker
                                  size="sm"
                                  value={override}
                                  onChange={(days) => {
                                    const next = { ...(config.platform_post_days || {}) };
                                    next[platform.key] = days;
                                    setConfig({ ...config, platform_post_days: next });
                                  }}
                                />
                              ) : (
                                <p className="text-[11px] text-[#9c9585]">Folgt den allgemeinen Wochentagen.</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            </section>

            {/* Favorite Templates */}
            <section id="settings-section-templates" data-settings-section="templates" className="scroll-mt-32">
            <Card className="glass border-[#e3dccb]">
              <CardHeader>
                <CardTitle className="text-[#23211a] flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#9a6d1f]" />
                  {t('autoPost.templates')}
                </CardTitle>
                <CardDescription>
                  {config.auto_select_template
                    ? t('autoPost.templatesDesc')
                    : t('autoPost.templatesDescManual')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-[#fffdf7] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#1f4d3a]" />
                    <span className="text-sm text-[#4a463b]">{t('autoPost.autoSelectTemplate')}</span>
                  </div>
                  <Switch
                    checked={config.auto_select_template}
                    onCheckedChange={(checked) => setConfig({ ...config, auto_select_template: checked })}
                    data-testid="auto-select-template-toggle"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleToggleTemplate(template.id)}
                      className={`p-4 rounded-lg cursor-pointer transition-all border-2 ${
                        config.favorite_templates?.includes(template.id)
                          ? 'border-[#9a6d1f] bg-[#f6edd8]'
                          : 'border-[#d8d0bd] bg-[#fffdf7] hover:border-[#1f4d3a]/40'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-[#23211a]">{template.name}</p>
                          <p className="text-xs text-[#8a8474] mt-1">
                            {t('autoPost.photos', { count: template.media_count })}
                            {template.has_voiceover && ' \u00b7 Voiceover'}
                          </p>
                        </div>
                        {config.favorite_templates?.includes(template.id) && (
                          <Star className="w-5 h-5 text-[#9a6d1f] fill-amber-400" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            </section>

            {/* Captions — AI caption/hashtag generation + tone/style tuning */}
            <section id="settings-section-captions" data-settings-section="captions" className="scroll-mt-32 space-y-4">
            <Card className="glass border-[#e3dccb]">
              <CardHeader>
                <CardTitle className="text-[#23211a] flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#1f4d3a]" />
                  {t('autoPost.nav.captions') || 'Captions'}
                </CardTitle>
                <CardDescription>{t('autoPost.captionsSectionDesc') || 'Wie die KI Bildunterschriften und Hashtags erstellt.'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[#fffdf7] rounded-lg">
                  <div>
                    <p className="text-sm text-[#4a463b]">{t('autoPost.autoCaptions')}</p>
                    <p className="text-xs text-[#8a8474]">{t('autoPost.autoCaptionsDesc')}</p>
                  </div>
                  <Switch
                    checked={config.auto_generate_caption}
                    onCheckedChange={(checked) => setConfig({ ...config, auto_generate_caption: checked })}
                    data-testid="auto-caption-toggle"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-[#fffdf7] rounded-lg">
                  <div>
                    <p className="text-sm text-[#4a463b]">{t('autoPost.autoHashtags')}</p>
                    <p className="text-xs text-[#8a8474]">{t('autoPost.autoHashtagsDesc')}</p>
                  </div>
                  <Switch
                    checked={config.auto_generate_hashtags}
                    onCheckedChange={(checked) => setConfig({ ...config, auto_generate_hashtags: checked })}
                    data-testid="auto-hashtags-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Caption tone/CTA/hashtag tuning + brand-voice analyzer. Lives
                in the Captions section alongside the AI generation toggles. */}
            <CaptionStyleSection
              captionStyle={config.caption_style || {}}
              onChange={(nextStyle) => setConfig({ ...config, caption_style: nextStyle })}
              propertyOptions={propertyOptions}
              onAutoSave={async (nextStyle) => {
                try {
                  const res = await autoPostAPI.updateConfig({ ...config, caption_style: nextStyle });
                  toast.success(t('autoPost.brandVoice.saved')
                    || 'Deine Stimme wird in zukünftige Captions einfließen.');
                  const fresh = res?.data || { ...config, caption_style: nextStyle };
                  setConfig(fresh);
                  setSavedConfigJson(JSON.stringify(fresh));
                } catch (err) {
                  console.warn('Auto-save of caption_style failed:', err);
                }
              }}
            />
            </section>

            {/* Voiceover — AI voice selection + script controls */}
            <section id="settings-section-voiceover" data-settings-section="voiceover" className="scroll-mt-32">
            <Card className="glass border-[#e3dccb]">
              <CardHeader>
                <CardTitle className="text-[#23211a] flex items-center gap-2">
                  <Mic className="w-5 h-5 text-[#8f6f3a]" />
                  {t('autoPost.nav.voiceover') || 'Voiceover'}
                </CardTitle>
                <CardDescription>{t('autoPost.voiceoverSectionDesc') || 'Die KI-Stimme, die den Sprechtext im Video spricht.'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-[#fffdf7] rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#4a463b]">{t('autoPost.enableVoiceover')}</p>
                    <p className="text-xs text-[#8a8474]">{t('autoPost.enableVoiceoverDesc')}</p>
                  </div>
                  <Switch
                    checked={config.enable_voiceover}
                    onCheckedChange={(checked) => setConfig({ ...config, enable_voiceover: checked })}
                    data-testid="enable-voiceover-toggle"
                  />
                </div>

                {/* Voice picker — curated preset voices or your own ElevenLabs
                    voice (with a how-to guide). */}
                <VoiceSelectSection
                  value={config.voice_id}
                  onChange={(vid) => setConfig({ ...config, voice_id: vid })}
                  disabled={!config.enable_voiceover}
                />

                {/* Voiceover script controls — shape what the AI actually
                    says (#14). Per-template tone is automatic. */}
                <div className={`pt-4 border-t border-[#e3dccb] space-y-3 ${!config.enable_voiceover ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div>
                      <label className="text-xs font-medium text-[#4a463b] block mb-1">
                        Eigene Anweisungen an die KI (optional)
                      </label>
                      <textarea
                        value={config.voiceover_instructions || ''}
                        onChange={(e) => setConfig({ ...config, voiceover_instructions: e.target.value.slice(0, 500) })}
                        rows={2}
                        maxLength={500}
                        placeholder='z.B. "Erwähne die ruhige Lage und den Garten, sprich die Familie direkt an."'
                        data-testid="voiceover-instructions"
                        className="w-full bg-[#f7f3ea]/60 border border-[#d8d0bd] rounded-md px-3 py-2 text-sm text-[#23211a] placeholder:text-[#9c9585] focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40 resize-none"
                      />
                      <p className="text-[10px] text-[#8a8474] mt-1">
                        Die KI wählt automatisch die relevantesten Verkaufsargumente für ~12&nbsp;Sekunden. Hier kannst du sie lenken. {(config.voiceover_instructions || '').length}/500
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-[#4a463b]">Firmennamen nennen</p>
                        <p className="text-[11px] text-[#8a8474]">Der Sprechtext endet mit deinem Firmennamen als Call-to-Action.</p>
                      </div>
                      <Switch
                        checked={config.voiceover_include_brand !== false}
                        onCheckedChange={(checked) => setConfig({ ...config, voiceover_include_brand: checked })}
                        data-testid="voiceover-include-brand"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-[#4a463b]">Preis/Zahlen erlauben</p>
                        <p className="text-[11px] text-[#8a8474]">Standardmäßig bleibt der Sprechtext emotional; Zahlen stehen im Bild. Aktivieren, um z.&nbsp;B. den Preis zu nennen.</p>
                      </div>
                      <Switch
                        checked={config.voiceover_mention_price === true}
                        onCheckedChange={(checked) => setConfig({ ...config, voiceover_mention_price: checked })}
                        data-testid="voiceover-mention-price"
                      />
                    </div>
                </div>
              </CardContent>
            </Card>
            </section>

            {/* Notifications — the preview e-mail sent before each post.
                A5 preview/approval mode: a guided radio group with an
                explainer recommending the strict→loose progression so
                first-week users don't fire bad AI output to the public. */}
            <section id="settings-section-notifications" data-settings-section="notifications" className="scroll-mt-32">
            <Card className="glass border-[#e3dccb]">
              <CardHeader>
                <CardTitle className="text-[#23211a] flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#1f4d3a]" />
                  {t('autoPost.nav.notifications') || 'Benachrichtigungen'}
                </CardTitle>
                <CardDescription>{t('autoPost.notificationsSectionDesc') || 'Die Vorschau-E-Mail, die du vor jedem geplanten Post bekommst.'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-[#f0f3ec] border border-[#1f4d3a]/30">
                    <Info className="w-5 h-5 text-[#1f4d3a] mt-0.5 shrink-0" />
                    <div className="text-xs text-[#4a463b] leading-relaxed">
                      <p className="font-medium text-[#2e6b50] mb-1">
                        {t('autoPost.previewModeIntroTitle') || 'Wie wollen wir posten?'}
                      </p>
                      <p>
                        {t('autoPost.previewModeIntroDesc') ||
                          'Wir empfehlen: Starte mit "Bestätigung erforderlich". Du bekommst 12 Stunden vor jedem Post eine E-Mail mit Vorschau und musst aktiv "Bestätigen" klicken. Wenn du nach ein paar guten Posts Vertrauen in die KI hast, kannst du auf "Nur benachrichtigen" oder "Komplett automatisch" umschalten.'}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2" role="radiogroup" aria-label={t('autoPost.previewModeLabel') || 'Vorschau-Modus'}>
                    {[
                      {
                        key: 'auto_with_preview',
                        title: t('autoPost.modeNotifyTitle') || 'Vorschau-E-Mail, dann automatisch posten',
                        body: t('autoPost.modeNotifyDesc') ||
                          'Du bekommst täglich eine E-Mail mit Vorschau. Reagierst du nicht, posten wir zur geplanten Zeit. Klickst du "Abbrechen", überspringen wir den Slot. Das ist die Standardeinstellung. Auto-Post läuft auch im Urlaub weiter.',
                        tone: 'violet',
                        recommended: true,
                      },
                      {
                        key: 'require_approval',
                        title: t('autoPost.modeStrictTitle') || 'Nur posten wenn ich bestätige',
                        body: t('autoPost.modeStrictDesc') ||
                          'Wir posten NUR, wenn du in der E-Mail "Bestätigen" oder "Jetzt veröffentlichen" klickst. Ignorierst du die E-Mail, fragen wir am nächsten Tag mit demselben Video erneut. Bis du dich entscheidest. Strikt, aber kein Slot geht verloren.',
                        tone: 'emerald',
                      },
                      {
                        key: 'auto_silent',
                        title: t('autoPost.modeSilentTitle') || 'Komplett automatisch (keine E-Mails)',
                        body: t('autoPost.modeSilentDesc') ||
                          'Set-and-forget: Wir posten ohne Vorschau-E-Mail. Wähle das nur, wenn du der KI komplett vertraust und keine Benachrichtigungen mehr willst.',
                        tone: 'slate',
                      },
                    ].map((option) => {
                      const selected = config.preview_mode === option.key;
                      const accentBorder =
                        selected && option.tone === 'emerald' ? 'border-[#1e7a4f] bg-[#e4f0e8]' :
                        selected && option.tone === 'violet'  ? 'border-[#1f4d3a] bg-[#e9efe7]' :
                        selected                              ? 'border-[#b5ad99] bg-[#b5ad99]/10' :
                                                                'border-[#d8d0bd] bg-[#f2ecdf] hover:border-[#1f4d3a]/50';
                      return (
                        <label
                          key={option.key}
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${accentBorder}`}
                        >
                          <input
                            type="radio"
                            name="preview_mode"
                            value={option.key}
                            checked={selected}
                            onChange={() => setConfig({ ...config, preview_mode: option.key })}
                            className="mt-1 accent-[#1f4d3a] cursor-pointer"
                            data-testid={`preview-mode-${option.key}`}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-[#23211a]">{option.title}</p>
                              {option.recommended && (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1f4d3a] text-white font-semibold">
                                  {t('autoPost.modeRecommended') || 'Empfohlen'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[#6f6a5c] mt-1 leading-relaxed">{option.body}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            </section>

            {/* Music section. Sits after Caption Style since both
                affect render output. Multi-select preset cards plus
                MP3 upload; scheduler picks randomly per render for
                sonic variety. */}
            <section id="settings-section-music" data-settings-section="music" className="scroll-mt-32">
            <AutoPostMusicSection
              selectedPresetIds={config.background_music_preset_ids || []}
              selectedUploadUrl={config.background_music_url || null}
              onChange={(patch) => setConfig({ ...config, ...patch })}
            />
            </section>
          </TabsContent>

          {/* ─── Queue Tab ─── */}
          <TabsContent value="queue" className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#23211a]">
                {t('autoPost.queue')} ({t('autoPost.queueCount', { count: queue.length })})
              </h3>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleRefresh} className="text-[#6f6a5c]">
                  <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                  {t('common.refresh')}
                </Button>
                <Button
                  onClick={handleGeneratePost}
                  size="sm"
                  className="bg-[#1f4d3a] hover:bg-[#163a2c]"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  {t('autoPost.generateNow')}
                </Button>
              </div>
            </div>

            {queue.length === 0 ? (
              <Card className="glass border-[#e3dccb]">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-[#9c9585] mx-auto mb-3" />
                  <p className="text-[#6f6a5c]">{t('autoPost.noQueue')}</p>
                  <p className="text-[#8a8474] text-sm mt-1">
                    {t('autoPost.noQueueDesc')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {queue.map((item) => (
                  <Card
                    key={item.id}
                    onClick={() => setDetailItem(item)}
                    className="glass border-[#e3dccb] hover:border-[#1f4d3a]/40 cursor-pointer transition-colors"
                    data-testid={`queue-row-${item.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#23211a] truncate">
                            {item.property_title || t('autoPost.property')}
                          </p>
                          {item.caption && (
                            <p className="text-xs text-[#8a8474] truncate mt-0.5">
                              {item.caption.slice(0, 80)}...
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-[#8a8474]">
                              {(() => {
                                // MongoDB serialises BSON Dates as naive ISO
                                // strings (no Z / +00:00). JS Date() then
                                // mis-parses them as LOCAL time, which made
                                // the queue display the UTC value as if it
                                // were local. A 15:00 Vienna slot showed
                                // up as "13:00". Append Z when no timezone
                                // marker is present so JS treats it as UTC.
                                const raw = item.scheduled_for;
                                if (!raw) return '–';
                                const s = String(raw);
                                const fixed = /[Zz]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z';
                                return new Date(fixed).toLocaleString('de-AT', {
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                });
                              })()}
                            </span>
                            <div className="flex gap-1">
                              {item.platforms?.map(p => (
                                <span key={p}>
                                  <PlatformIcon platform={p} className="w-3 h-3" />
                                </span>
                              ))}
                            </div>
                          </div>
                          {item.status === 'failed' && item.error_message && (
                            <p className="text-xs text-[#b3543f] mt-1 flex items-center gap-1">
                              <XCircle className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{item.error_message}</span>
                            </p>
                          )}
                        </div>

                        {/* stopPropagation on this cluster so clicking
                            the retry/delete buttons doesn't also fire
                            the row-click → open-modal handler. */}
                        <div
                          className="flex items-center gap-2 ml-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                            item.status === 'pending' ? 'bg-[#f0e2c2] text-[#9a6d1f]' :
                            item.status === 'generating_video' ? 'bg-[#e9efe7] text-[#1f4d3a]' :
                            item.status === 'preview_pending' ? 'bg-sky-600/20 text-sky-400' :
                            item.status === 'video_ready' ? 'bg-[#f0e6cf] text-[#8f6f3a]' :
                            item.status === 'posting' ? 'bg-blue-600/20 text-blue-400' :
                            item.status === 'failed' ? 'bg-[#a34430]/20 text-[#b3543f]' :
                            'bg-[#c9c1ac]/20 text-[#6f6a5c]'
                          }`}>
                            {item.status === 'generating_video' && <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />}
                            {/* v1.6 – `preview_pending` covers both modes
                                with very different UX. In require_approval
                                the queue is waiting on the user; in
                                auto_with_preview the post fires by itself.
                                Pick the right copy off the user's config
                                so the queue row never says "waiting for
                                your approval" to someone whose post will
                                ship regardless. */}
                            {(() => {
                              const key = item.status === 'preview_pending'
                                  && config.preview_mode !== 'require_approval'
                                ? 'autoPost.status.preview_pending_auto'
                                : `autoPost.status.${item.status}`;
                              return t(key) || item.status;
                            })()}
                          </span>

                          {item.status === 'failed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRetryQueueItem(item.id)}
                              className="text-[#9a6d1f] hover:text-[#9a6d1f] hover:bg-[#f6edd8] h-8 w-8 p-0"
                              title={t('common.retry')}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}

                          {/* Allow user to cancel/skip an item that's
                              waiting in pending/preview_pending. Without
                              this, require_approval users with a
                              carry-over item that they don't want to
                              post anymore had no way to skip it from
                              the UI. They'd have to wait for the next
                              cycle (or worse, click decline in the
                              email each day). Failed items also get a
                              delete button so the queue stays tidy. */}
                          {['pending', 'preview_pending', 'failed'].includes(item.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteQueueItem(item.id)}
                              className="text-[#b3543f] hover:text-[#a34430] hover:bg-[#f7e8e3] h-8 w-8 p-0"
                              title={t('common.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ─── History Tab ─── */}
          <TabsContent value="history" className="space-y-4 mt-6">
            {/* Stats Cards */}
            {history.stats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="glass border-[#e3dccb]">
                  <CardContent className="p-4 text-center">
                    <BarChart3 className="w-6 h-6 text-[#1f4d3a] mx-auto mb-1" />
                    <p className="text-2xl font-bold text-[#23211a]">{history.stats.total_posts || 0}</p>
                    <p className="text-xs text-[#8a8474]">{t('autoPost.totalPosts')}</p>
                  </CardContent>
                </Card>
                <Card className="glass border-[#e3dccb]">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-[#1e7a4f] mx-auto mb-1" />
                    <p className="text-2xl font-bold text-[#23211a]">{history.stats.success_rate || 0}%</p>
                    <p className="text-xs text-[#8a8474]">{t('autoPost.successRate')}</p>
                  </CardContent>
                </Card>
                <Card className="glass border-[#e3dccb]">
                  <CardContent className="p-4 text-center">
                    <AlertCircle className="w-6 h-6 text-[#b3543f] mx-auto mb-1" />
                    <p className="text-2xl font-bold text-[#23211a]">{history.stats.total_failed || 0}</p>
                    <p className="text-xs text-[#8a8474]">{t('autoPost.failed')}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* History List */}
            {(!history.items || history.items.length === 0) ? (
              <Card className="glass border-[#e3dccb]">
                <CardContent className="p-8 text-center">
                  <History className="w-12 h-12 text-[#9c9585] mx-auto mb-3" />
                  <p className="text-[#6f6a5c]">{t('autoPost.noHistory')}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {history.items.map((item) => (
                  <Card
                    key={item.id}
                    onClick={() => setDetailItem(item)}
                    className="glass border-[#e3dccb] hover:border-[#1f4d3a]/40 cursor-pointer transition-colors"
                    data-testid={`history-row-${item.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#23211a] truncate">
                            {item.property_title || t('autoPost.property')}
                          </p>
                          {item.caption && (
                            <p className="text-xs text-[#8a8474] truncate mt-0.5">
                              {item.caption.slice(0, 80)}...
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-[#8a8474]">
                              {item.completed_at
                                ? new Date(item.completed_at).toLocaleString()
                                : t('autoPost.unknown')}
                            </span>
                            <div className="flex gap-1">
                              {item.platforms?.map(p => (
                                <span key={p}>
                                  <PlatformIcon platform={p} className="w-3 h-3" />
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <CheckCircle className="w-5 h-5 text-[#1e7a4f] ml-4 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Floating "unsaved changes" save bar. Appears at the bottom
            of the viewport when the user's config differs from the
            last-saved snapshot. Fixes the long-page UX problem where
            users had to scroll back to the top to save. Includes
            Save + Discard so the user can revert without reloading.
            Auto-hides on successful save. */}
        <FloatingSaveBar
          visible={isDirty}
          saving={saving}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          t={t}
        />

        {/* Queue/History item detail modal. Opens when the user
            clicks a row. Shows the video player + per-platform
            editable captions for in-flight items, read-only for
            completed/failed/rejected. Approve/Reject directly from
            here (no need to chase the email link). On change, we
            update the queue or history list so the row reflects
            new state immediately. */}
        {detailItem && (
          <AutoPostItemDetailModal
            item={detailItem}
            previewMode={config.preview_mode || 'auto_with_preview'}
            onClose={() => setDetailItem(null)}
            onChange={(updated) => {
              if (!updated) return;
              // The item could live in either the queue or the
              // history list. Try to update wherever it is.
              setQueue((rows) => rows.map((r) => r.id === updated.id ? { ...r, ...updated } : r));
              setHistory((h) => {
                if (!h?.items) return h;
                return { ...h, items: h.items.map((r) => r.id === updated.id ? { ...r, ...updated } : r) };
              });
              // Reflect the updated state in the open modal so
              // users see the latest captions/status without re-opening.
              setDetailItem((cur) => cur && cur.id === updated.id ? { ...cur, ...updated } : cur);
            }}
            onDelete={handleDeleteQueueItem}
          />
        )}
      </div>
    </AppLayout>
  );
}


/* ─── Connected sources summary ─────────────────────────────────────
   Slim card at the top of Auto-Post settings showing which import
   sources are connected. Both Justimmo + Willhaben management lives
   on /imports. This just surfaces "is it connected?" with one click
   to go manage. Keeps Auto-Post settings focused on schedule + content. */
function ConnectedSourcesSummary({ t, navigate }) {
  const [justimmoConnected, setJustimmoConnected] = useState(null);
  const [willhabenSubscribed, setWillhabenSubscribed] = useState(null);
  const [willhabenName, setWillhabenName] = useState(null);

  useEffect(() => {
    Promise.all([
      justimmoExportAPI.getStatus().catch(() => ({ data: { connected: false } })),
      autoPostAPI.getConfig().catch(() => ({ data: {} })),
    ]).then(([js, ap]) => {
      setJustimmoConnected(!!js.data?.connected);
      setWillhabenSubscribed(!!ap.data?.willhaben_subscription_org_id);
      setWillhabenName(ap.data?.willhaben_subscription_org_name || null);
    });
  }, []);

  const anyConnected = justimmoConnected || willhabenSubscribed;
  const loading = justimmoConnected === null;

  return (
    <Card className="glass border-[#e3dccb]">
      <CardHeader>
        <CardTitle className="text-[#23211a] flex items-center gap-2">
          <Database className="w-5 h-5 text-[#1f4d3a]" />
          {t("autoPost.sourcesTitle") || "Datenquellen"}
        </CardTitle>
        <CardDescription>
          {t("autoPost.sourcesDesc") || "Auto-Post nutzt deine importierten Immobilien. Verwalte die Quellen unter „Datenquellen“."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#8a8474]" />
            ) : !anyConnected ? (
              <span className="inline-flex items-center gap-1 text-[#9a6d1f]">
                <AlertCircle className="w-3.5 h-3.5" />
                {t("autoPost.noSourcesYet") || "Noch keine Quelle verbunden"}
              </span>
            ) : (
              <>
                {justimmoConnected && (
                  <span className="inline-flex items-center gap-1 text-[#1e7a4f] px-2 py-1 rounded-full border border-[#1e7a4f]/40 bg-[#e4f0e8]">
                    <CheckCircle className="w-3 h-3" />
                    Justimmo
                  </span>
                )}
                {willhabenSubscribed && (
                  <span className="inline-flex items-center gap-1 text-[#1e7a4f] px-2 py-1 rounded-full border border-[#1e7a4f]/40 bg-[#e4f0e8]">
                    <CheckCircle className="w-3 h-3" />
                    Willhaben{willhabenName ? ` · ${willhabenName}` : ""}
                  </span>
                )}
              </>
            )}
          </div>
          <Button
            size="sm"
            variant={anyConnected ? "ghost" : "default"}
            onClick={() => navigate("/imports")}
            className={anyConnected ? "text-[#4a463b] hover:bg-[#efe9db]" : "bg-[#1f4d3a] hover:bg-[#163a2c] text-white"}
          >
            {anyConnected
              ? (t("autoPost.manageSources") || "Verwalten")
              : (t("autoPost.connectSource") || "Quelle verbinden")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


/* ─── Settings tab sub-navigation ───────────────────────────────────
   Sticky pill bar that scroll-jumps to each settings section. With 7+
   sections the page is long; the pills act as a table of contents +
   a jump nav. Scroll-spy highlights the section currently in view.

   Pills wrap on small viewports. Each pill targets `#settings-section-<key>`
   anchors which are placed on the section wrappers in the JSX above.

   Sticky position sits below the AppLayout header (top-16 = 64px).
   Z-index below header (z-40 < z-50) so AppLayout's header overlaps
   cleanly on smaller screens. */
function SettingsTabNav({ t }) {
  const [activeId, setActiveId] = useState(null);

  // Section definitions. Id matches `settings-section-<id>` anchors.
  // Icon name maps to a lucide component below. Translation keys are
  // already namespaced in autoPost.nav.* so adding/renaming a section
  // is a one-place change.
  const sections = useMemo(() => [
    { id: 'sources',       icon: Database,    labelKey: 'autoPost.nav.sources',       fallback: 'Quellen' },
    { id: 'schedule',      icon: Clock,       labelKey: 'autoPost.nav.schedule',      fallback: 'Plan' },
    { id: 'platforms',     icon: Globe,       labelKey: 'autoPost.nav.platforms',     fallback: 'Plattformen' },
    { id: 'templates',     icon: Star,        labelKey: 'autoPost.nav.templates',     fallback: 'Vorlagen' },
    { id: 'captions',      icon: BarChart3,   labelKey: 'autoPost.nav.captions',      fallback: 'Captions' },
    { id: 'voiceover',     icon: Mic,         labelKey: 'autoPost.nav.voiceover',     fallback: 'Voiceover' },
    { id: 'notifications', icon: Mail,        labelKey: 'autoPost.nav.notifications', fallback: 'Benachrichtigungen' },
    { id: 'music',         icon: Music2,      labelKey: 'autoPost.nav.music',         fallback: 'Musik' },
  ], []);

  // Scroll-spy. Pick the "most recent section the user has scrolled
  // past" as active.
  //
  // Previous implementation used IntersectionObserver entries +
  // "smallest top among isIntersecting". That's fragile because:
  //   (a) the callback only carries entries that JUST CHANGED state,
  //       so the computed "smallest top" doesn't account for sections
  //       whose state didn't change but should still be considered;
  //   (b) for tall sections like Templates (700+px with the 11-card
  //       grid), the bounding rect math gets messy when the section
  //       spans multiple viewport heights.
  // Bug user reported: scrolling down through Templates would
  // sometimes flicker back to Plattformen.
  //
  // New algorithm: IntersectionObserver serves only as a "scroll
  // changed something" trigger. Inside the callback we query ALL
  // section bounding rects fresh and find the one whose top edge is
  // closest to (but not below) a reference line at viewport-top +
  // header offset. Same semantic browsers use to highlight TOC items
  // on docs sites. Deterministic, no fighting with thresholds.
  useEffect(() => {
    const nodes = sections
      .map((s) => document.querySelector(`[data-settings-section="${s.id}"]`))
      .filter(Boolean);
    if (!nodes.length) return;

    const byNode = new Map();
    nodes.forEach((node, i) => byNode.set(node, sections[i].id));

    // Reference line at viewport top + AppLayout header (64px) +
    // this sticky nav (~52px) + small breathing room. A section is
    // "passed" once its top has scrolled above this line.
    const REFERENCE_OFFSET = 130;

    const computeActive = () => {
      let mostRecentPast = null;
      let mostRecentPastDistance = -Infinity;   // closest-to-zero negative wins
      let firstUpcoming = null;
      let firstUpcomingDistance = Infinity;

      for (const node of nodes) {
        const top = node.getBoundingClientRect().top - REFERENCE_OFFSET;
        if (top <= 0) {
          // Past the reference line. We've scrolled into/through it.
          // Pick the LATEST one (top closest to 0 from below).
          if (top > mostRecentPastDistance) {
            mostRecentPastDistance = top;
            mostRecentPast = node;
          }
        } else {
          // Still below the reference line. Fallback for the case
          // where we're at the very top of the page and no section
          // has reached the reference yet.
          if (top < firstUpcomingDistance) {
            firstUpcomingDistance = top;
            firstUpcoming = node;
          }
        }
      }

      const active = mostRecentPast || firstUpcoming;
      if (active) {
        const id = byNode.get(active);
        if (id) setActiveId((prev) => (prev === id ? prev : id));
      }
    };

    // IO is just a "scroll likely changed which sections are in view"
    // trigger. The actual decision uses live getBoundingClientRect
    // reads inside computeActive. We DON'T trust the entries' rects
    // because they reflect the scroll position when the observer
    // queued the callback, not necessarily "now."
    const observer = new IntersectionObserver(() => computeActive(), {
      rootMargin: '0px 0px 0px 0px',
      threshold: [0, 0.5, 1],
    });
    nodes.forEach((n) => observer.observe(n));

    // Belt-and-braces: also recompute on scroll directly (rAF-
    // throttled). IO doesn't fire when you scroll WITHIN a fully-
    // visible section, but the active-section choice can change in
    // that case if the reference line crosses another section's top.
    let rafId = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        computeActive();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Initial state
    computeActive();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [sections]);

  const scrollToSection = (id) => {
    const el = document.querySelector(`[data-settings-section="${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Mark active immediately so the highlight doesn't lag behind
      // the smooth scroll.
      setActiveId(id);
    }
  };

  return (
    <nav
      className="sticky top-16 z-40 -mx-4 px-4 sm:mx-0 sm:px-0 py-2 backdrop-blur-md bg-[#f7f3ea]/90 border-y border-[#e3dccb] sm:border sm:rounded-lg sm:bg-[#f2ecdf]"
      aria-label="Settings sections"
    >
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1">
        {sections.map(({ id, icon: Icon, labelKey, fallback }) => {
          const active = activeId === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => scrollToSection(id)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                active
                  ? 'bg-[#dfe8db] text-[#23211a] border-[#1f4d3a]/50 shadow-[0_0_0_3px_rgba(139,92,246,0.08)]'
                  : 'bg-[#fffdf7] text-[#6f6a5c] border-[#d8d0bd] hover:border-[#1f4d3a]/50 hover:text-[#23211a]'
              }`}
              data-testid={`settings-nav-${id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="whitespace-nowrap">{t(labelKey) || fallback}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}


/* ─── Floating "unsaved changes" save bar ───────────────────────────
   Slides up from the bottom of the viewport when the user has
   pending edits. Has Save + Discard buttons + a small "Du hast
   Änderungen" chip. Auto-hides after a successful save (parent's
   isDirty flips false).

   Why floating rather than sticky-in-page: the page is long, the
   header save button is far. Users were editing settings deep
   in the page and having to scroll back up. A bottom-fixed bar
   stays in reach regardless of scroll position. Matches the
   pattern of GitHub's PR review bar / Google Docs comment bar.

   Mobile: full-width across the bottom, buttons stack vertically
   only as a last resort (label + actions inline by default).
   Desktop: centered with max-width so it doesn't span 4K screens.

   The bar's CSS `transform: translateY(*)` animates the slide-up/
   down. When invisible we render `null` so it's not even in the
   tree (saves a paint cycle on every config update). */
function FloatingSaveBar({ visible, saving, onSave, onDiscard, t }) {
  // Detect "small viewport" so we can hide the long descriptor text
  // on phones (just keep the buttons).
  if (!visible) return null;
  return (
    <div
      role="region"
      aria-label={t('autoPost.unsavedBar.aria') || 'Ungespeicherte Änderungen'}
      className="fixed inset-x-0 bottom-0 z-40 pointer-events-none animate-slide-up"
    >
      <div className="mx-auto max-w-3xl pointer-events-auto px-3 pb-3 sm:pb-4">
        <div className="bg-[#fffdf7]/95 backdrop-blur-md border border-[#1f4d3a]/40 rounded-xl shadow-2xl shadow-[#1f4d3a]/10 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-shrink">
            {/* Animated dot. Subtle pulsing violet so the bar reads
                as "live state" rather than a static toast. */}
            <span className="relative inline-flex w-2 h-2 shrink-0">
              <span className="absolute inset-0 rounded-full bg-[#2e6b50] animate-ping opacity-60" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-[#2e6b50]" />
            </span>
            <p className="text-sm text-[#23211a] truncate">
              <span className="hidden sm:inline">
                {t('autoPost.unsavedBar.title') || 'Du hast ungespeicherte Änderungen'}
              </span>
              <span className="sm:hidden">
                {t('autoPost.unsavedBar.titleShort') || 'Änderungen ungespeichert'}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onDiscard}
              disabled={saving}
              className="text-[#6f6a5c] hover:text-[#23211a] hover:bg-[#efe9db]"
              data-testid="floating-discard-btn"
            >
              {t('autoPost.unsavedBar.discard') || 'Verwerfen'}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onSave}
              disabled={saving}
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
              data-testid="floating-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  {t('common.saving') || 'Speichere…'}</>
              ) : (
                <>{t('common.save') || 'Speichern'}</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
